import moment from 'moment';
import { TopicRadarData } from '../types/Widgets';
import { StudyHistoryRecord } from '../slices/studyHistory/studyHistorySlice';

export const TOPIC_RADAR_FAKE_DATA: TopicRadarData = {
  MSK: {
    correct: 10,
    total: 10,
    percentage: 90,
  },
  Endo: {
    correct: 10,
    total: 10,
    percentage: 87,
  },
  Basic: {
    correct: 10,
    total: 10,
    percentage: 92,
  },
  GI: {
    correct: 10,
    total: 10,
    percentage: 60,
  },
  Derm: {
    correct: 10,
    total: 10,
    percentage: 54,
  },
  Repro: {
    correct: 10,
    total: 10,
    percentage: 70,
  },
  Renal: {
    correct: 10,
    total: 10,
    percentage: 65,
  },
  Cardio: {
    correct: 10,
    total: 10,
    percentage: 70,
  },
  Psych: {
    correct: 10,
    total: 10,
    percentage: 69,
  },
  Neuro: {
    correct: 10,
    total: 10,
    percentage: 55,
  },
  Heme: {
    correct: 10,
    total: 10,
    percentage: 50,
  },
  Pulm: {
    correct: 10,
    total: 10,
    percentage: 67,
  },
  Immune: {
    correct: 10,
    total: 10,
    percentage: 78,
  },
  Ethics: {
    correct: 10,
    total: 10,
    percentage: 85,
  },
  ID: {
    correct: 10,
    total: 10,
    percentage: 90,
  },
};

/**
 * Generates realistic fake study history data for a given number of days
 * @param days - Number of days in the past to generate data for
 * @returns StudyHistoryRecord with realistic study patterns
 */
export function generateDemoStudyHistoryData(days: number): StudyHistoryRecord {
  const data: StudyHistoryRecord = {};

  for (let i = 0; i < days; i++) {
    const date = moment().subtract(i, 'day').format('YYYY-MM-DD');
    const dayOfWeek = moment().subtract(i, 'day').day(); // 0 = Sunday, 6 = Saturday
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    // Increased rest day probability: 15% overall, 25% on weekends
    const restDayChance = isWeekend ? 0.2 : 0.1;
    const isRestDay = Math.random() < restDayChance;
    // Light study day: 20% chance when not a rest day
    const isLightStudyDay = !isRestDay && Math.random() < 0.2;
    // Base performance improves over time (recent days have better scores)
    const timeMultiplier = Math.max(0.3, 1 - (i / days) * 0.6);
    const baseScore = 40 + timeMultiplier * 30 + (Math.random() * 20 - 10);
    const finalScore = Math.max(0, Math.min(100, baseScore));
    // Generate qbank data (0-30 range)
    let qbankTotal: number;
    let qbankCorrect: number;
    let qbankScore: number;

    if (isRestDay) {
      qbankTotal = 0;
      qbankCorrect = 0;
      qbankScore = 0;
    } else if (isLightStudyDay) {
      // Light study: 1-8 questions
      qbankTotal = Math.round(1 + Math.random() * 7);
      qbankScore = Math.round(finalScore);
      qbankCorrect = Math.round((qbankTotal * qbankScore) / 100);
    } else {
      // Normal study: 5-30 questions with higher variance
      const baseQuestions = isWeekend ? 3 : 8;
      const maxVariation = isWeekend ? 12 : 22; // More variance

      qbankTotal = Math.round(
        baseQuestions + Math.random() * maxVariation * timeMultiplier
      );

      qbankTotal = Math.min(30, qbankTotal); // Cap at 30
      qbankScore = Math.round(finalScore);
      qbankCorrect = Math.round((qbankTotal * qbankScore) / 100);
    }

    // Generate flashcard data (0-50 range)
    let flashcardsTotal: number;

    if (isRestDay) {
      flashcardsTotal = 0;
    } else if (isLightStudyDay) {
      // Light study: 3-12 flashcards
      flashcardsTotal = Math.round(3 + Math.random() * 9);
    } else {
      // Normal study: 10-50 flashcards with high variance
      const baseFlashcards = isWeekend ? 8 : 15;
      const maxVariation = isWeekend ? 20 : 35; // Much more variance

      flashcardsTotal = Math.round(
        baseFlashcards + Math.random() * maxVariation * timeMultiplier
      );

      flashcardsTotal = Math.min(50, flashcardsTotal); // Cap at 50
    }

    // Generate videos data (0-1 range with 30% chance)
    let videosTotal: number;

    if (isRestDay) {
      videosTotal = 0;
    } else {
      // 30% chance of 1 video, otherwise 0
      videosTotal = Math.random() < 0.3 ? 1 : 0;
    }

    data[date] = {
      qbank: {
        total: qbankTotal,
        correct: qbankCorrect,
        score: qbankScore,
      },
      flashcards: {
        total: flashcardsTotal,
      },
      videos: {
        total: videosTotal,
        started: videosTotal,
      },
    };
  }

  return data;
}
